import { type ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import axios from 'axios'

import apiClient from '@/lib/api-client'
import { API_BASE } from '@/lib/env'
import { useAuthStore } from '@/lib/store'
import { buildAuthPath, getSafeAuthRedirect, setStoredAuthRedirect } from '@/features/auth/redirect'

interface Props {
  children: ReactNode
  requireStaff?: boolean
  requireKmpAdmin?: boolean
}

type SessionStatus = 'checking' | 'unauthenticated' | 'ready'

let sessionBootstrapPromise: Promise<void> | null = null

function hasCompleteUserPayload() {
  const { user } = useAuthStore.getState()
  return Boolean(user && typeof user.is_kmp_admin === 'boolean')
}

async function bootstrapSessionOnce() {
  const authState = useAuthStore.getState()
  if (authState.isAuthenticated && hasCompleteUserPayload()) {
    return
  }

  if (!authState.isAuthenticated) {
    const refreshRes = await axios.post(
      `${API_BASE}/api/v1/auth/token/refresh/`,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    useAuthStore.getState().setAccessToken(refreshRes.data.access)
  }

  const userRes = await apiClient.get('/api/v1/users/me/')
  useAuthStore.getState().setUser(userRes.data)
}

function getSessionBootstrapPromise() {
  if (!sessionBootstrapPromise) {
    sessionBootstrapPromise = bootstrapSessionOnce().finally(() => {
      sessionBootstrapPromise = null
    })
  }
  return sessionBootstrapPromise
}

export default function ProtectedRoute({ children, requireStaff, requireKmpAdmin }: Props) {
  const location = useLocation()
  const { isAuthenticated, logout, user } = useAuthStore()
  const hasUserPayload = Boolean(user && typeof user.is_kmp_admin === 'boolean')
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>(() =>
    isAuthenticated && hasUserPayload ? 'ready' : 'checking',
  )

  useEffect(() => {
    if (sessionStatus === 'unauthenticated') {
      return
    }

    if (isAuthenticated && hasUserPayload) {
      setSessionStatus('ready')
      return
    }

    let active = true

    getSessionBootstrapPromise()
      .then(() => {
        if (!active) return
        setSessionStatus('ready')
      })
      .catch(() => {
        if (!active) return

        logout()
        setSessionStatus('unauthenticated')
      })

    return () => {
      active = false
    }
  }, [hasUserPayload, isAuthenticated, logout, sessionStatus])

  if (sessionStatus === 'checking') {
    return null
  }

  if (sessionStatus === 'unauthenticated' || !isAuthenticated) {
    const currentPath = `${location.pathname}${location.search}${location.hash}`
    const nextPath = getSafeAuthRedirect(currentPath)
    setStoredAuthRedirect(nextPath)
    return <Navigate to={buildAuthPath('/login', nextPath)} replace />
  }

  if (requireStaff && !user?.is_staff) {
    return <Navigate to="/" replace />
  }

  if (requireKmpAdmin && !user?.is_kmp_admin) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
